import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams, } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { formatDate } from "../../../utils/FormatUtils";
import { getLocalGridState, setLocalGridState, } from "../../../utils/GridUtils";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import ColouredChip from "../../../component/ColouredChip";
import { Button, IconButton, Stack, Tooltip } from "@mui/material";
import { AddRounded, DescriptionRounded } from "@mui/icons-material";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import { useNavigate } from "react-router-dom";
import { STATE_ACTIVE_FILTERS, STATE_TABLE_UPDATING } from "../constants/CardRecoil";
import { CARD_STATUSES, convertTypology, NETWORK_CODE } from "../constants/CardConstants";
import { useCardsRepository } from "../../../api/repositories/AmexCardsRepository";
import { useAgGridLocale, useHasScope } from "../../../../../config/globalConfig";
import { AMEXSOF_ADD_CARD, AMEXSOF_VIEW_CARD_DETAIL } from "../../../utils/FinanceScope";


const CardTable = () => {
    const UNIQUE_ID = "fnn-sdd-amex-cards";

    const { t } = useTranslation("finance", { keyPrefix: "payments.amex.cards" });

    const navigate = useNavigate();
    const hasScope = useHasScope;
    const { getCards } = useCardsRepository();

    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);

    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);

    const columnDefs: ColDef[] = [
        {
            field: 'customerNameOrCompany', headerName: t('table.customer'), headerTooltip: t('table.customer'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'customerSapId', headerName: t('table.idSap'), headerTooltip: t('table.idSap'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'networkCode', headerName: t('table.networkCode'), headerTooltip: t('table.networkCode'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => t(convertTypology(NETWORK_CODE, params.value))
        },
        {
            field: 'pan', headerName: t('table.pan'), headerTooltip: t('table.pan'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'panAlias', headerName: t('table.panAlias'), headerTooltip: t('table.panAlias'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'panAliasCustomer', headerName: t('table.customerPanAlias'), headerTooltip: t('table.customerPanAlias'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: "expirationDate",
            headerName: t("table.expirationDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "status",
            headerName: t("table.status"),
            minWidth: 100,
            unSortIcon: true,
            sortable: true,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                if (!status) status = "UNMANAGED";
                let color = CARD_STATUSES.find(s => s.value === status)?.color?.split(".")[0];
                let tooltipText: string = params.data.errorReason;
                if (!color || color === "neutral") color = "default";
                return {
                    component: () => (
                            <ColouredChip name={status} color={color} tooltip={tooltipText}/>
                    ),
                };
            },
        },
        {
            field: "legalEntity",
            headerName: t("table.legalEntity"),
            minWidth: 180,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "dealer",
            headerName: t("table.dealer"),
            minWidth: 180,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                let visible = params.data.status === "ERROR" ? true : false;
                return {
                    component: () =>

                            <FinanceDynamicActions>
                                {hasScope(AMEXSOF_VIEW_CARD_DETAIL) &&
                                        <Tooltip arrow placement={"top"} title={t("table.actions.details")}>
                                            <IconButton color="accent" disabled={visible} onClick={() => {
                                                window.open(`/finance/payments/amex/cards/wizard/${params.data.id}`, "_blank");
                                            }}>
                                                <DescriptionRounded/>
                                            </IconButton>
                                        </Tooltip>
                                }
                            </FinanceDynamicActions>
                };
            },
            suppressFiltersToolPanel: true
        }
    ];

    const toolbar = {
        renderInfo: () => (<></>),
        renderActions: () => (
                <Stack>
                    {hasScope(AMEXSOF_ADD_CARD) &&
                            <Button fullWidth color={"accent"} variant="contained" component="label"
                                    startIcon={<AddRounded/>}
                                    onClick={() => {
                                        navigate('/finance/payments/amex/cards/wizard')
                                    }}>
                                {t('buttons.create')}
                            </Button>
                    }

                </Stack>

        ),
    };

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
            headerCheckboxSelection: true
        },
        noRowsOverlayComponentParams: {
            title: t("table.no-rows.title"),
            content: t("table.no-rows.content")
        },
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(
                createDatasource(filterDefs, gridRef.current.api)
        );
        setSummaryUpdating(true);
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(
                    createDatasource(filterDefs, gridRef.current.api)
            );
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating]);

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                        params.request.sortModel.length === 0
                                ? null
                                : params.request.sortModel[0].colId;
                let order =
                        params.request.sortModel.length === 0
                                ? null
                                : params.request.sortModel[0].sort.toUpperCase();
                getCards(startRow, endRow - startRow, sort, order, filterDefs)
                        .then((response) => {
                            let result = response.result;

                            if (result["total-amount"] === 0) {
                                api.showNoRowsOverlay();
                            } else {
                                api.hideOverlay();
                            }

                            params.success({
                                rowData: result.content,
                                rowCount: result["total-amount"],
                            });
                        })
                        .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState(),
        });
    }

    return (
            <>
                <AgGridCustomTable
                        toolbar={toolbar}
                        rowModelType="serverSide"
                        paginationPageSize={getLocalGridState(UNIQUE_ID).rows}
                        serverSideInfiniteScroll
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        gridOptions={gridOptions}
                        rowSelection={"multiple"}
                        suppressRowClickSelection
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        onGridReady={() => {
                            gridRef.current!.api.setServerSideDatasource(
                                    createDatasource(filterDefs, gridRef.current!.api)
                            );

                            let gridState = getLocalGridState(UNIQUE_ID).cols;
                            if (gridState !== undefined) {
                                gridRef.current!.columnApi.applyColumnState({
                                    state: gridState,
                                    applyOrder: true,
                                });
                            }
                        }}
                        onColumnMoved={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnPinned={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnResized={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onSortChanged={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onPaginationChanged={() => {
                            if (gridRef.current!.api) {
                                setLocalGridState(UNIQUE_ID, {
                                    rows: gridRef.current!.api?.paginationGetPageSize(),
                                    cols: getLocalGridState(UNIQUE_ID).cols,
                                });
                            }
                        }}
                ></AgGridCustomTable>


            </>
    );
};

export default CardTable;
